import React, { useMemo, useState, useEffect, useImperativeHandle, forwardRef, useCallback, useRef } from "react";
import { PlayerSearch } from "./PlayerSearch";
import { fmtNum, positionColor } from "@/lib/format";
import { X, Users, Plus, ListPlus } from "lucide-react";

function withRosterInstanceKeys(roster) {
  return (roster?.players || []).map((asset, index) => ({
    ...asset,
    rosterInstanceKey: `${roster.roster_id}-${index}-${String(asset.id || asset.name).replace(/[^a-zA-Z0-9_-]/g, "_")}`,
  }));
}

function getTradeItemKey(asset, fallbackIndex) {
  return asset.tradeItemKey || asset.rosterInstanceKey || asset.finderKey || `${asset.id}-${fallbackIndex}`;
}

function availableRosterAssets(roster, sideItems) {
  const inSide = new Set(sideItems.map((asset, index) => asset.rosterInstanceKey || getTradeItemKey(asset, index)));
  return withRosterInstanceKeys(roster)
    .filter((asset) => asset.combinedValue > 0 && !inSide.has(asset.rosterInstanceKey));
}

function TeamPicker({ roster, rosterAssets, items, onAdd, onLoadAll, testId }) {
  const inSide = new Set(items.map((p, index) => p.rosterInstanceKey || getTradeItemKey(p, index)));
  const available = [...rosterAssets]
    .filter((p) => !inSide.has(p.rosterInstanceKey))
    .sort((a, b) => (b.combinedValue || 0) - (a.combinedValue || 0));
  return (
    <div className="border border-[#0A0A0A]/20 bg-[#FAFAFA]" data-testid={`${testId}-team-picker`}>
      <div className="flex items-center justify-between px-3 py-2 border-b border-[#0A0A0A]/15">
        <span className="text-[10px] uppercase tracking-widest text-[#52525B] font-mono-data">
          {roster.team_name} · pick assets
        </span>
        <button
          onClick={onLoadAll}
          className="text-[10px] uppercase tracking-widest border border-[#0A0A0A]/40 hover:bg-[#0A0A0A] hover:text-white px-2 py-1 transition-colors inline-flex items-center gap-1"
          data-testid={`${testId}-load-all`}
        >
          <ListPlus size={11} /> Add all
        </button>
      </div>
      <div className="max-h-[220px] overflow-auto p-2 space-y-1">
        {available.length === 0 && (
          <div className="text-xs text-[#52525B] py-3 text-center">All assets added.</div>
        )}
        {available.map((p) => (
          <button
            key={p.rosterInstanceKey}
            onClick={() => onAdd(p)}
            className="w-full flex items-center justify-between gap-2 px-2 py-1.5 border border-dashed border-[#0A0A0A]/15 hover:border-[#0A0A0A] hover:border-solid hover:bg-white transition-colors text-left text-[#52525B] hover:text-[#0A0A0A]"
            data-testid={`${testId}-pick-${p.rosterInstanceKey}`}
          >
            <div className="flex items-center gap-2 min-w-0 flex-1">
              <span className={`text-[9px] font-bold px-1 py-0.5 ${positionColor(p.position)} opacity-80`}>{p.position}</span>
              <span className="truncate text-xs">{p.name}</span>
            </div>
            <span className="font-mono-data text-[11px] tabular-nums">{fmtNum(p.combinedValue)}</span>
            <Plus size={12} className="text-[#7C3AED]" />
          </button>
        ))}
      </div>
    </div>
  );
}

function BalanceSuggestions({ suggestions, diff, onAdd }) {
  if (!suggestions.length) return null;

  const leader = diff > 0 ? "Side A" : "Side B";
  const trailer = diff > 0 ? "Side B" : "Side A";

  return (
    <div className="brutal-card mt-4" data-testid="balance-suggestions">
      <div className="border-b border-[#0A0A0A] px-4 py-3 flex items-center justify-between gap-3 flex-wrap">
        <div>
          <span className="label-eyebrow">Balance Suggestions</span>
          <div className="text-xs text-[#52525B] font-mono-data mt-1">
            {leader} leads by {fmtNum(Math.abs(diff))}. Add one asset to {trailer} to even it out.
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2 p-3">
        {suggestions.map((suggestion) => (
          <button
            key={suggestion.asset.rosterInstanceKey}
            onClick={() => onAdd(suggestion.side, suggestion.asset)}
            className="flex items-center justify-between gap-3 border border-dashed border-[#0A0A0A]/25 hover:border-[#0A0A0A] hover:border-solid hover:bg-[#FAFAFA] px-3 py-2 transition-colors text-left"
            data-testid={`balance-suggestion-${suggestion.asset.rosterInstanceKey}`}
          >
            <div className="flex items-center gap-2 min-w-0 flex-1">
              <span className={`text-[9px] font-bold px-1 py-0.5 ${positionColor(suggestion.asset.position)}`}>{suggestion.asset.position}</span>
              <div className="min-w-0">
                <div className="text-sm font-semibold truncate">{suggestion.asset.name}</div>
                <div className="text-[10px] text-[#52525B] font-mono-data">
                  Add to Side {suggestion.side} · off by {fmtNum(suggestion.distance)}
                </div>
              </div>
            </div>
            <span className="font-mono-data text-xs tabular-nums font-bold">{fmtNum(suggestion.asset.combinedValue)}</span>
            <Plus size={13} className="text-[#7C3AED]" />
          </button>
        ))}
      </div>
    </div>
  );
}

function Side({ title, items, onAdd, onRemove, players, testId, rosters, teamId, onTeamChange, onLoadAll, playerToTeam }) {
  const total = items.reduce((s, p) => s + (p.combinedValue || 0), 0);
  const selectedRoster = teamId ? rosters?.find((r) => r.roster_id === teamId) : null;
  const [rosterFilterDisabled, setRosterFilterDisabled] = useState(false);
  const rosterAssets = useMemo(() => withRosterInstanceKeys(selectedRoster), [selectedRoster]);
  const selectedRosterInstances = useMemo(
    () => new Set(items.map((p, index) => p.rosterInstanceKey || getTradeItemKey(p, index))),
    [items]
  );
  const availableRosterAssets = useMemo(
    () => rosterAssets.filter((asset) => !selectedRosterInstances.has(asset.rosterInstanceKey)),
    [rosterAssets, selectedRosterInstances]
  );
  const searchPlayers = selectedRoster && !rosterFilterDisabled ? availableRosterAssets : players;

  useEffect(() => {
    setRosterFilterDisabled(false);
  }, [teamId]);

  const handlePick = (p) => {
    let assetToAdd = p;
    // If no team is selected and the picked player belongs to a team in this league,
    // set the team context so the rest of that team's roster appears in the picker.
    if (!teamId && playerToTeam) {
      const owningRosterId = playerToTeam.get(String(p.id));
      if (owningRosterId) {
        const owningRoster = rosters?.find((r) => r.roster_id === owningRosterId);
        const owningRosterAsset = withRosterInstanceKeys(owningRoster).find((asset) => String(asset.id) === String(p.id));
        if (owningRosterAsset) assetToAdd = owningRosterAsset;
        onTeamChange(owningRosterId, { keepItems: true });
      }
    }
    onAdd(assetToAdd);
  };

  return (
    <div className="brutal-card flex flex-col" data-testid={`${testId}-side`}>
      <div className="border-b border-[#0A0A0A] px-4 py-3 flex items-center justify-between gap-2">
        <span className="label-eyebrow">{title}</span>
        <span className="font-mono-data text-xs text-[#52525B]">{items.length} {items.length === 1 ? "asset" : "assets"}</span>
      </div>

      {rosters && rosters.length > 0 && (
        <div className="px-4 pt-3">
          <label className="label-eyebrow flex items-center gap-1 mb-1.5"><Users size={10} /> Team</label>
          <select
            value={teamId || ""}
            onChange={(e) => onTeamChange(e.target.value ? Number(e.target.value) : null)}
            className="w-full px-2.5 py-2 text-xs border border-[#0A0A0A]/30 focus:border-[#7C3AED] outline-none bg-white font-mono-data"
            data-testid={`${testId}-team-select`}
          >
            <option value="">Any player…</option>
            {rosters.map((r) => {
              const star = r.is_me ? " *" : "";
              const valStr = Number(r.total_value || 0).toLocaleString();
              return (
                <option key={r.roster_id} value={r.roster_id}>
                  {`${r.team_name}${star} · ${valStr}`}
                </option>
              );
            })}
          </select>
        </div>
      )}

      <div className="p-4 flex flex-col gap-3 min-h-[200px]">
        <PlayerSearch
          players={searchPlayers}
          onPick={handlePick}
          testId={`${testId}-search`}
          placeholder={selectedRoster && !rosterFilterDisabled ? `Search ${selectedRoster.team_name}…` : "Add player or pick…"}
          showDisableRosterFilter={Boolean(selectedRoster && !rosterFilterDisabled)}
          onDisableRosterFilter={() => setRosterFilterDisabled(true)}
        />

        {selectedRoster && (
          <TeamPicker roster={selectedRoster} rosterAssets={rosterAssets} items={items} onAdd={onAdd} onLoadAll={onLoadAll} testId={testId} />
        )}

        <div className="flex flex-col gap-2" data-testid={`${testId}-items`}>
          {items.length === 0 && !selectedRoster && (
            <div className="text-xs text-[#52525B] py-6 text-center border border-dashed border-[#0A0A0A]/30">
              No assets added yet.
            </div>
          )}
          {items.map((p, index) => {
            const itemKey = getTradeItemKey(p, index);
            return (
            <div key={itemKey} className="flex items-center justify-between gap-3 border-l-4 border-l-[#7C3AED] border-y border-r border-[#0A0A0A] bg-[#F5F0FF] hover:bg-[#EDE3FF] px-3 py-2 transition-colors shadow-[2px_2px_0_0_#0A0A0A]" data-testid={`${testId}-item-${itemKey}`}>
              <div className="flex items-center gap-2 min-w-0 flex-1">
                <span className={`text-[10px] font-bold px-1.5 py-0.5 ${positionColor(p.position)}`}>{p.position}</span>
                <div className="min-w-0">
                  <div className="text-sm font-semibold truncate text-[#0A0A0A]">{p.name}</div>
                  <div className="text-[11px] text-[#52525B] font-mono-data">
                    {p.team || "—"}{p.combinedRank ? ` · #${p.combinedRank}` : ""}
                  </div>
                </div>
              </div>
              <span className="font-mono-data text-sm tabular-nums font-bold text-[#0A0A0A]">{fmtNum(p.combinedValue)}</span>
              <button onClick={() => onRemove(p.id, index)} className="text-[#52525B] hover:text-[#FF3B30] transition-colors" aria-label="Remove" data-testid={`${testId}-remove-${itemKey}`}>
                <X size={16} />
              </button>
            </div>
            );
          })}
        </div>
      </div>
      <div className="mt-auto border-t border-[#0A0A0A] px-4 py-3 flex items-center justify-between bg-[#FAFAFA]">
        <span className="label-eyebrow">Total Value</span>
        <span className="font-display font-black text-3xl tabular-nums" data-testid={`${testId}-total`}>{fmtNum(total)}</span>
      </div>
    </div>
  );
}

export const TradeCalculator = forwardRef(function TradeCalculator({ players, leagueDetail }, ref) {
  const [sideA, setSideA] = useState([]);
  const [sideB, setSideB] = useState([]);
  const [sideATeamId, setSideATeamId] = useState(null);
  const [sideBTeamId, setSideBTeamId] = useState(null);
  const tradeItemCounter = useRef(0);

  // Map of sleeper-roster player.id → roster_id (for auto-team detection on search-pick)
  const playerToTeam = useMemo(() => {
    const m = new Map();
    if (leagueDetail?.rosters) {
      for (const r of leagueDetail.rosters) {
        for (const p of r.players) {
          if (p.id) m.set(String(p.id), r.roster_id);
        }
      }
    }
    return m;
  }, [leagueDetail]);

  // Sync Side A team context to user's team on league change (but DO NOT auto-load players)
  useEffect(() => {
    if (!leagueDetail) {
      setSideATeamId(null);
      setSideBTeamId(null);
      setSideA([]);
      setSideB([]);
      return;
    }
    const myTeam = leagueDetail.rosters.find(r => r.is_me);
    if (myTeam) setSideATeamId(myTeam.roster_id);
    setSideBTeamId(null);
    setSideA([]);
    setSideB([]);
  }, [leagueDetail]);

  const totalA = useMemo(() => sideA.reduce((s, p) => s + (p.combinedValue || 0), 0), [sideA]);
  const totalB = useMemo(() => sideB.reduce((s, p) => s + (p.combinedValue || 0), 0), [sideB]);

  const diff = totalA - totalB;
  const max = Math.max(totalA, totalB) || 1;
  const pctDiff = (Math.abs(diff) / max) * 100;
  const balanceSuggestions = useMemo(() => {
    if (!leagueDetail || totalA === totalB || (sideA.length === 0 && sideB.length === 0)) return [];

    const gap = Math.abs(diff);
    const sideToAdd = diff > 0 ? "B" : "A";
    const rosterId = sideToAdd === "A" ? sideATeamId : sideBTeamId;
    const roster = leagueDetail.rosters?.find((r) => r.roster_id === rosterId);
    if (!roster) return [];

    const sideItems = sideToAdd === "A" ? sideA : sideB;
    return availableRosterAssets(roster, sideItems)
      .map((asset) => ({
        side: sideToAdd,
        asset,
        distance: Math.abs((asset.combinedValue || 0) - gap),
      }))
      .sort((a, b) => a.distance - b.distance || (b.asset.combinedValue || 0) - (a.asset.combinedValue || 0))
      .slice(0, 6);
  }, [diff, leagueDetail, sideA, sideATeamId, sideB, sideBTeamId, totalA, totalB]);

  let verdict = "EVEN";
  let verdictColor = "#0A0A0A";
  let verdictBg = "#FACC15";
  let winner = null;
  if (sideA.length === 0 && sideB.length === 0) {
    verdict = "ADD ASSETS";
    verdictBg = "#F3F4F6";
  } else if (pctDiff < 5) {
    verdict = "FAIR";
  } else {
    winner = diff > 0 ? "A" : "B";
    if (pctDiff < 15) verdict = `SIDE ${winner} WINS`;
    else if (pctDiff < 30) verdict = `SIDE ${winner} WINS BIG`;
    else verdict = `SIDE ${winner} HEIST`;
    verdictBg = "#00C853";
  }

  const setTeamContextFromPlayer = useCallback((side, p) => {
    const currentTeamId = side === "A" ? sideATeamId : sideBTeamId;
    if (currentTeamId || !playerToTeam) return;
    const owningRosterId = playerToTeam.get(String(p.id));
    if (!owningRosterId) return;
    if (side === "A") setSideATeamId(owningRosterId);
    else setSideBTeamId(owningRosterId);
  }, [playerToTeam, sideATeamId, sideBTeamId]);

  const prepareTradeItem = useCallback((side, asset) => ({
    ...asset,
    tradeItemKey: asset.rosterInstanceKey || asset.finderKey || `free-${side}-${String(asset.id || asset.name).replace(/[^a-zA-Z0-9_-]/g, "_")}-${tradeItemCounter.current++}`,
  }), []);

  const addTo = useCallback((side, p) => {
    const item = prepareTradeItem(side, p);
    const addItem = (prev) => {
      if (item.rosterInstanceKey && prev.some((x) => x.rosterInstanceKey === item.rosterInstanceKey)) return prev;
      return [...prev, item];
    };
    if (side === "A") setSideA(addItem);
    else setSideB(addItem);
    setTeamContextFromPlayer(side, p);
  }, [prepareTradeItem, setTeamContextFromPlayer]);
  const removeFrom = (side, id, itemIndex = null) => {
    const removeOne = (prev) => (
      itemIndex === null
        ? prev.filter(x => x.id !== id)
        : prev.filter((_, index) => index !== itemIndex)
    );
    if (side === "A") setSideA(removeOne);
    else setSideB(removeOne);
  };

  const handleTeamChange = (side, newRosterId, opts = {}) => {
    if (side === "A") {
      setSideATeamId(newRosterId);
      if (!opts.keepItems) setSideA([]);
    } else {
      setSideBTeamId(newRosterId);
      if (!opts.keepItems) setSideB([]);
    }
  };

  const loadAllFromTeam = (side) => {
    const tid = side === "A" ? sideATeamId : sideBTeamId;
    const roster = leagueDetail?.rosters?.find(r => r.roster_id === tid);
    if (!roster) return;
    const rosterItems = withRosterInstanceKeys(roster).map((asset) => prepareTradeItem(side, asset));
    if (side === "A") setSideA(rosterItems);
    else setSideB(rosterItems);
  };

  // Imperative API used by LeaguesPanel (still loads whole team when called directly)
  const loadTeam = useCallback((side, roster) => {
    if (!roster) {
      if (side === "A") { setSideATeamId(null); setSideA([]); }
      else { setSideBTeamId(null); setSideB([]); }
      return;
    }
    const rosterItems = withRosterInstanceKeys(roster).map((asset) => prepareTradeItem(side, asset));
    if (side === "A") { setSideATeamId(roster.roster_id); setSideA(rosterItems); }
    else { setSideBTeamId(roster.roster_id); setSideB(rosterItems); }
  }, [prepareTradeItem]);

  const startTradeFromFinder = useCallback((myRoster, targetRoster, targetPlayer, outgoingAssets = []) => {
    setSideATeamId(myRoster?.roster_id || null);
    setSideA(outgoingAssets.map((asset) => prepareTradeItem("A", asset)));
    setSideBTeamId(targetRoster?.roster_id || null);
    setSideB(targetPlayer ? [prepareTradeItem("B", targetPlayer)] : []);
  }, [prepareTradeItem]);

  useImperativeHandle(ref, () => ({
    addToSide: addTo,
    loadTeam,
    startTradeFromFinder,
  }), [addTo, loadTeam, startTradeFromFinder]);

  const reset = () => {
    setSideA([]); setSideB([]);
    setSideATeamId(null); setSideBTeamId(null);
  };

  const rosters = leagueDetail?.rosters || null;

  return (
    <section data-testid="trade-calculator" className="relative">
      <div className="flex items-end justify-between mb-4 flex-wrap gap-3">
        <div>
          <span className="label-eyebrow" data-testid="trade-calculator-eyebrow">01 / Trade Calculator</span>
          <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tighter mt-1">Build the deal.</h2>
          {leagueDetail && (
            <p className="text-xs text-[#52525B] mt-2 font-mono-data uppercase tracking-widest">
              Linked · {leagueDetail.name}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button onClick={reset} className="brutal-btn-outline px-4 py-2 text-xs uppercase tracking-widest" data-testid="trade-reset">Reset</button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto_1fr] gap-4 lg:gap-0 relative">
        <Side
          title="Side A — You Give"
          items={sideA}
          onAdd={(p) => addTo("A", p)}
          onRemove={(id) => removeFrom("A", id)}
          players={players}
          testId="side-a"
          rosters={rosters}
          teamId={sideATeamId}
          onTeamChange={(rid, opts) => handleTeamChange("A", rid, opts)}
          onLoadAll={() => loadAllFromTeam("A")}
          playerToTeam={playerToTeam}
        />
        <div className="hidden lg:flex items-center justify-center px-2 relative z-10">
          <div className="brutal-card p-4 min-w-[180px] text-center shadow-[6px_6px_0_0_#0A0A0A]" style={{ background: verdictBg, color: verdictColor }} data-testid="trade-verdict">
            <div className="label-eyebrow" style={{ color: verdictColor === "#0A0A0A" ? "#52525B" : verdictColor }}>Verdict</div>
            <div className="font-display font-black text-xl mt-1 tracking-tight">{verdict}</div>
            <div className="font-mono-data text-xs mt-2 tabular-nums">Δ {fmtNum(Math.abs(diff))} ({pctDiff.toFixed(1)}%)</div>
          </div>
        </div>
        <Side
          title="Side B — You Get"
          items={sideB}
          onAdd={(p) => addTo("B", p)}
          onRemove={(id) => removeFrom("B", id)}
          players={players}
          testId="side-b"
          rosters={rosters}
          teamId={sideBTeamId}
          onTeamChange={(rid, opts) => handleTeamChange("B", rid, opts)}
          onLoadAll={() => loadAllFromTeam("B")}
          playerToTeam={playerToTeam}
        />
        <div className="lg:hidden col-span-1 brutal-card p-4 text-center shadow-[4px_4px_0_0_#0A0A0A]" style={{ background: verdictBg, color: verdictColor }} data-testid="trade-verdict-mobile">
          <div className="label-eyebrow">Verdict</div>
          <div className="font-display font-black text-xl mt-1 tracking-tight">{verdict}</div>
          <div className="font-mono-data text-xs mt-2 tabular-nums">Δ {fmtNum(Math.abs(diff))} ({pctDiff.toFixed(1)}%)</div>
        </div>
      </div>

      <BalanceSuggestions
        suggestions={balanceSuggestions}
        diff={diff}
        onAdd={addTo}
      />
    </section>
  );
});
